/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import ContentCopy from '@hyphengroup/cms-components/components/icons/ContentCopy';
import {
  LeadCapturePage_attributeList,
  LeadCapturePage_loanCalculationResult,
  RewardProduct_Detail,
  GetProductByUidOperation,
  LeadCapturePage_keyValueTextAttribute,
} from '__generated__/graphql';
import { createLazyQuery } from 'car-insurance/hooks/useLazyQuery';
import classNames from 'classnames';
import { JSX, useEffect, useRef, useState } from 'react';
import { first } from 'utils/array';
import { Translated } from 'utils/staticText';
import {
  trackLeadCaptureClick,
  MembershipRewardsTrackingData,
} from 'utils/tracking';
import { getVerticalNameFromProductUid } from '../apply/[productId]/lib/product';
import { RewardProductText } from './i18n';
import ProductAttribute from './ProductAttribute';
import { RewardProductDialog } from './RewardProductDialog';
import ArrowRightAlt from '$/components/icons/ArrowRightAlt';
import Article from '$/components/icons/Article';
import InfoCircle from '$/components/icons/InfoCircle';

type RewardProductProps = {
  variant: 'VARIANT_A' | 'VARIANT_B';
};

type RangeValue = {
  minimum?: number;
  maximum?: number;
};

type additionalAttributeType = {
  prefix?: string;
  suffix?: string;
  emptyValueText?: string;
  value?: number | string;
  label?: string;
  attributeFormat: string;
  attributeType?: string;
  additionalInformation?: string;
  fees?: {
    annualFlatRate?: number;
    apr?: number;
  }[];
  rangeValue?: RangeValue;
  position?: number;
};

type LoanData = {
  currencyPrefix?: string;
  amount?: number;
  tenure?: number;
  calculation?: LeadCapturePage_loanCalculationResult;
};

export function RewardProduct(props: {
  product: Omit<RewardProduct_Detail, 'showPromoCodePopup'> & {
    showPromoCodePopup?: boolean;
    twoStepApplicationEnabled?: boolean;
    twoStepApplicationContent?: {
      applicationTitle?: string;
      applicationSubtitle?: string;
      applicationFooter?: string;
      productSectionTitle?: string;
    };
  };
  attributes: LeadCapturePage_attributeList['attributeList'];
  staticText: Translated<typeof RewardProductText>;
  variant: RewardProductProps['variant'];
  loanData?: LoanData;
  market: string;
}): JSX.Element {
  const [showProductDetail, setShowProductDetail] = useState<boolean>(false);
  const [showPromoCodeReminderPopup, setShowPromoCodeReminderPopup] =
    useState<boolean>(false);
  const tagLabelMobileRef = useRef<HTMLSpanElement>(null);
  const [tagLabelMobileHeight, setTagLabelMobileHeight] = useState(0);
  const heightTagLabelMobile = 30;
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);

  const catalogueProduct = props.product?.catalogueProduct;
  const {
    dialogSeeMore,
    dialogSeeLess,
    dialogRewardsTitle,
    dialogRewardsDescription,
    dialogRewardsOption,
    dialogRewardsTC,
    eligibility,
    fees,
  } = props.staticText;

  const productCardTopBadge =
    props.product?.catalogueProduct?.productCardTopBadge;
  const tag =
    productCardTopBadge?.__typename === 'KeyValueTextAttribute'
      ? first(first(productCardTopBadge.value)?.values)
      : undefined;

  const tracking: MembershipRewardsTrackingData = {
    campaignId: props.product.productId,
    campaignName: props.product.productDisplayName,
    product: {
      productUid: props.product?.catalogueProduct?.productUid,
      productName: props.product?.catalogueProduct?.productName,
      providerName: props.product?.catalogueProduct?.provider?.providerName,
    },
  };

  useEffect(() => {
    const checkTagLabelHeight = () => {
      const tagLabelMobile = tagLabelMobileRef.current;
      if (tagLabelMobile) {
        const currentHeight = tagLabelMobile.clientHeight;
        setTagLabelMobileHeight(currentHeight);
      }
    };

    checkTagLabelHeight();

    const resizeListener = () => checkTagLabelHeight();
    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('resize', checkTagLabelHeight);
    };
  }, []);

  const isCC =
    getVerticalNameFromProductUid(catalogueProduct?.productUid) === 'CC';

  const promoCodeAttribute = props.attributes?.find(
    (item) => item?.attributeType === 'PROMO_CODE',
  ) as additionalAttributeType;

  const isShowPromoCodeReminderPopup = props.product?.showPromoCodePopup;
  useEffect(() => {
    if (
      promoCodeAttribute &&
      promoCodeAttribute?.value &&
      isShowPromoCodeReminderPopup
    ) {
      setShowPromoCodeReminderPopup(true);
    }
  }, [promoCodeAttribute, isShowPromoCodeReminderPopup]);

  const handleCopyPromoCode = async () => {
    if (promoCodeAttribute && promoCodeAttribute?.value) {
      const promoCodeAttributeValue = String(promoCodeAttribute.value ?? '');

      if (promoCodeAttributeValue) {
        try {
          await navigator.clipboard.writeText(promoCodeAttributeValue);
          setShowCopiedTooltip(true);
          setTimeout(() => {
            setShowCopiedTooltip(false);
          }, 1500);
        } catch (err) {
          console.error('Failed to copy promo code:', err);
        }
      }
    }
  };

  return (
    <section className="mhg-alpha-rewards-lead-capture-section">
      <div className="mhg-alpha-display-1 not-italic text-[--text-primary]">
        {props.product?.twoStepApplicationEnabled &&
        props.product?.twoStepApplicationContent?.productSectionTitle
          ? props.product.twoStepApplicationContent.productSectionTitle
          : props.staticText.productHeading}
      </div>
      <div className="mhg-alpha-rewards-lead-capture-card">
        <div className="mhg-alpha-rewards-lead-capture-card-content">
          <div
            className={`${props.variant === 'VARIANT_A' ? 'mhg-alpha-rewards-lead-capture-card-content-heading' : 'mhg-alpha-rewards-lead-capture-card-content-heading-v2'}`}
          >
            {tag && (
              <div className="mhg-alpha-rewards-lead-capture-card-content-tag">
                <span
                  ref={tagLabelMobileRef}
                  className={`mhg-alpha-tag-font flex min-h-[--tag-size] items-center justify-center gap-[--button-gap] bg-[--highlights-green-light] text-[--colors-supplementary-green-dark] md:hidden ${tagLabelMobileHeight < heightTagLabelMobile ? 'rounded-[--tag-size] px-[--tag-padding-x] py-0 text-center' : 'rounded-[--radius-button] px-[--button-padding-regular-x-icon] py-[--spacing-padding-y-xs] text-left'}`}
                >
                  {tag}
                </span>
                <span className="mhg-alpha-tag-font hidden min-h-[--tag-size] items-center justify-center gap-[--button-gap] rounded-[--tag-size] px-[--tag-padding-x] py-0 text-center bg-[--highlights-green-light] text-[--colors-supplementary-green-dark] md:flex">
                  {tag}
                </span>
              </div>
            )}
            <div className="mhg-alpha-rewards-lead-capture-card-image-wrapper">
              <div
                className={classNames(
                  'flex flex-col items-center justify-center gap-[--spacing-gap-x] bg-[--fill-card]',
                  {
                    'p-3 rounded-[--radius-card-inner] border border-solid border-[--borders-default]':
                      !isCC,
                  },
                )}
              >
                <img
                  className="h-[--provider-asset-logo-container-max-height] object-contain"
                  alt={
                    catalogueProduct?.productName ??
                    props.product?.productDisplayName
                  }
                  src={
                    catalogueProduct?.productImage ??
                    props.product?.productImage
                  }
                />
              </div>
              <div className="flex flex-1 shrink-0 basis-0 flex-col justify-center gap-[--spacing-gap-y-sm] self-stretch">
                <div className="mhg-alpha-title-1 md:mhg-alpha-display-1 not-italic text-[--text-primary]">
                  {catalogueProduct?.productName ??
                    props.product?.productDisplayName}
                </div>
                {promoCodeAttribute && promoCodeAttribute?.value && (
                  <div className="flex origin-left items-center gap-spacing/gap/soft-4px">
                    <p className="mhg-alpha-typography-body-2 md:mhg-alpha-typography-body-1 text-nowrap text-text/secondary-lighter">
                      {props.staticText.promoCode}:
                    </p>
                    <p className="mhg-alpha-typography-bullet-2 md:mhg-alpha-typography-bullet-1 text-fill/primary">
                      {promoCodeAttribute?.value}
                    </p>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={handleCopyPromoCode}
                    >
                      <ContentCopy className="cursor-pointer" />
                      {showCopiedTooltip && (
                        <div className="mhg-alpha-typography-caption absolute z-10 rounded p-2 shadow-lg bg-white text-text/secondary-lighter">
                          {props.staticText.copied}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {props.product.productVertical?.code === 'ULNS'
          ? renderLoanProductAttributes(
              props.attributes,
              props.market,
              props.staticText,
              props.loanData,
            )
          : renderProductAttributes(props.attributes)}

        <div className="mhg-alpha-rewards-lead-capture-card-footer">
          <button
            type="button"
            className="mhg-alpha-rewards-lead-capture-card-footer-content !py-4"
            onClick={() => {
              setShowProductDetail(true);
              trackLeadCaptureClick(
                'card',
                `Product Card_See All Product Details`,
                tracking,
                'lead-submission',
                1,
                'product_interaction',
              );
            }}
          >
            <Article className="mhg-alpha-rewards-lead-capture-card-footer-content-icon" />
            <span className="mhg-alpha-rewards-lead-capture-card-footer-content-label">
              {isCC
                ? props.staticText.viewFullCardsDetails
                : props.staticText.productSeeAllDetail}
            </span>
          </button>
        </div>
      </div>
      {props.product?.twoStepApplicationEnabled && (
        <RenderTwoStepApplicationContent
          productAttributes={props.attributes}
          twoStepApplicationContent={props.product.twoStepApplicationContent}
        />
      )}
      <RewardProductDialog
        setOpen={setShowProductDetail}
        isOpen={showProductDetail}
        product={props.product}
        attributes={props.attributes}
        staticText={{
          dialogSeeMore,
          dialogSeeLess,
          dialogRewardsTitle,
          dialogRewardsDescription,
          dialogRewardsOption,
          dialogRewardsTC,
          eligibility,
          fees,
        }}
      />
      {showPromoCodeReminderPopup && (
        <RenderPromoCodeReminderPopup
          staticText={props.staticText}
          setOpen={setShowPromoCodeReminderPopup}
          promoCode={promoCodeAttribute?.value as string}
        />
      )}
    </section>
  );
}

export function renderProductAttributes(
  productAttributes: LeadCapturePage_attributeList['attributeList'],
): JSX.Element {
  const highlightedAttributes: additionalAttributeType[] = productAttributes
    ?.filter((item) => item?.isHighlighted)
    .map((item) => ({
      ...item,
    })) as additionalAttributeType[];

  return (
    <>
      <div className="mhg-alpha-rewards-lead-capture-card-attributes">
        {highlightedAttributes
          ?.filter((_, index) => index % 2 === 0)
          .map((attr) =>
            attr ? (
              <ProductAttribute
                key={attr.attributeType}
                label={attr.label}
                value={getAttributeValue(attr)}
                prefix={prefixAttribute(attr.prefix)}
                suffix={suffixAttribute(attr.suffix)}
                tooltipContent={attr.additionalInformation}
                classNameWrapper="col-1 flex items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
                classNameLabel="w-[--general-2-col-width]"
              />
            ) : null,
          )}
        {highlightedAttributes
          ?.filter((_, index) => index % 2 !== 0)
          .map((attr) =>
            attr ? (
              <ProductAttribute
                key={attr.attributeType}
                label={attr.label}
                value={getAttributeValue(attr)}
                prefix={prefixAttribute(attr.prefix)}
                suffix={suffixAttribute(attr.suffix)}
                tooltipContent={attr.additionalInformation}
                classNameWrapper="col-2 flex items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
                classNameLabel="w-[--general-2-col-width]"
              />
            ) : null,
          )}
      </div>

      {/* product attributes for mobile view */}
      <div className="mhg-alpha-rewards-lead-capture-card-attributes-v2">
        <div className="flex flex-col items-start gap-[--spacing-gap-y-sm] self-stretch">
          {highlightedAttributes?.map((attr) =>
            attr ? (
              <ProductAttribute
                key={attr.attributeType}
                label={attr.label}
                value={getAttributeValue(attr)}
                prefix={prefixAttribute(attr.prefix)}
                suffix={suffixAttribute(attr.suffix)}
                tooltipContent={attr.additionalInformation}
                classNameWrapper="flex items-start self-stretch gap-[--spacing-gap-x]"
                classNameLabel="w-[--provider-asset-size-1]"
              />
            ) : null,
          )}
        </div>
      </div>
    </>
  );
}

export function renderLoanProductAttributes(
  productAttributes: LeadCapturePage_attributeList['attributeList'],
  market: string,
  staticText: Translated<typeof RewardProductText>,
  loanData?: LoanData,
): JSX.Element {
  const loanRateAttr = productAttributes?.find(
    (item) => item?.attributeType === 'LOAN_RATES',
  ) as additionalAttributeType;

  const aprAttr = productAttributes?.find(
    (item) => item?.attributeType === 'APR',
  ) as additionalAttributeType;

  const annualInterestRateAttr = productAttributes?.find(
    (item) => item?.attributeType === 'ANNUALLY_FLAT_RATE',
  ) as additionalAttributeType;

  const monthlyRepaymentAttr = productAttributes?.find(
    (item) => item?.attributeType === 'MONTHLY_REPAYMENT',
  ) as additionalAttributeType;

  const totalRepaymentAttr = productAttributes?.find(
    (item) => item?.attributeType === 'TOTAL_REPAYMENT',
  ) as additionalAttributeType;

  const totalInterestAttr = productAttributes?.find(
    (item) => item?.attributeType === 'TOTAL_INTEREST',
  ) as additionalAttributeType;

  const loanCalculation = loanData?.calculation;

  const loanTenureValue =
    market === 'singsaver'
      ? loanTenureToMonths(loanData?.tenure)
      : `${loanData?.tenure} ${staticText.loanTenureMonth}`;

  return (
    <>
      <div className="mhg-alpha-rewards-lead-capture-card-attributes">
        <div className="flex flex-1 shrink-0 basis-0 flex-col items-start">
          <ProductAttribute
            label={staticText.loanAmountText}
            value={`${loanData?.currencyPrefix}${loanData?.amount?.toLocaleString()}`}
            classNameWrapper="flex h-[--button-size-regular] items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
            classNameLabel="w-[--general-2-col-width]"
          />
          {annualInterestRateAttr?.label &&
            first(loanRateAttr?.fees)?.annualFlatRate && (
              <ProductAttribute
                attributeType={annualInterestRateAttr?.attributeType}
                label={annualInterestRateAttr?.label}
                value={first(loanRateAttr?.fees)?.annualFlatRate}
                prefix={annualInterestRateAttr?.prefix}
                suffix={annualInterestRateAttr?.suffix}
                classNameWrapper="flex h-[--button-size-regular] items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
                classNameLabel="w-[--general-2-col-width]"
                isHighlight={true}
              />
            )}
          {market === 'singsaver' &&
            monthlyRepaymentAttr?.label &&
            loanCalculation?.monthlyRepayment && (
              <ProductAttribute
                attributeType={monthlyRepaymentAttr?.attributeType}
                label={monthlyRepaymentAttr?.label}
                prefix={monthlyRepaymentAttr?.prefix}
                suffix={monthlyRepaymentAttr?.suffix}
                value={Number(loanCalculation.monthlyRepayment).toLocaleString(
                  'en-SG',
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  },
                )}
                classNameWrapper="flex h-[--button-size-regular] items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
                classNameLabel="w-[--general-2-col-width]"
              />
            )}

          {market === 'moneyhero' &&
            totalRepaymentAttr?.label &&
            loanCalculation?.totalRepayment && (
              <ProductAttribute
                attributeType={totalRepaymentAttr?.attributeType}
                label={totalRepaymentAttr?.label}
                prefix={totalRepaymentAttr?.prefix}
                suffix={totalRepaymentAttr?.suffix}
                value={`${Number(loanCalculation.totalRepayment).toLocaleString(
                  'zh-HK',
                  {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  },
                )}`}
                classNameWrapper="flex h-[--button-size-regular] items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
                classNameLabel="w-[--general-2-col-width]"
              />
            )}

          {market === 'moneyhero' &&
            totalInterestAttr?.label &&
            loanCalculation?.totalInterest && (
              <ProductAttribute
                attributeType={totalInterestAttr?.attributeType}
                label={totalInterestAttr?.label}
                prefix={totalInterestAttr?.prefix}
                suffix={totalInterestAttr?.suffix}
                value={Number(loanCalculation.totalInterest).toLocaleString(
                  'zh-HK',
                  {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  },
                )}
                classNameWrapper="flex h-[--button-size-regular] items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
                classNameLabel="w-[--general-2-col-width]"
              />
            )}
        </div>
        <div className="flex flex-1 shrink-0 basis-0 flex-col items-start">
          <ProductAttribute
            label={staticText.loanTenureText}
            value={loanTenureValue}
            classNameWrapper="col-2 flex items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
            classNameLabel="w-[--general-2-col-width]"
          />
          {aprAttr?.label && first(loanRateAttr?.fees)?.apr && (
            <ProductAttribute
              attributeType={aprAttr?.attributeType}
              label={aprAttr?.label}
              value={first(loanRateAttr?.fees)?.apr}
              prefix={aprAttr?.prefix}
              suffix={aprAttr?.suffix}
              tooltipContent={aprAttr?.additionalInformation}
              classNameWrapper="col-2 flex items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
              classNameLabel="w-[--general-2-col-width]"
            />
          )}
          {market === 'singsaver' && (
            <ProductAttribute
              label={staticText.processingFeeText}
              value={`${loanData?.currencyPrefix}${loanCalculation?.totalFees ?? '0.00'}`}
              classNameWrapper="col-2 flex items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
              classNameLabel="w-[--general-2-col-width]"
            />
          )}
        </div>
      </div>

      {/* personal loan product attributes for mobile view */}
      <div className="mhg-alpha-rewards-lead-capture-card-attributes-v2">
        <div className="flex flex-col items-start gap-[--spacing-gap-y-sm] self-stretch">
          <ProductAttribute
            label={staticText.loanAmountText}
            value={`${loanData?.currencyPrefix}${loanData?.amount?.toLocaleString()}`}
            classNameWrapper="col-2 flex items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
            classNameLabel="w-[--general-2-col-width]"
          />
          {annualInterestRateAttr?.label &&
            first(loanRateAttr?.fees)?.annualFlatRate && (
              <ProductAttribute
                attributeType={annualInterestRateAttr?.attributeType}
                label={annualInterestRateAttr?.label}
                value={first(loanRateAttr?.fees)?.annualFlatRate}
                prefix={annualInterestRateAttr?.prefix}
                suffix={annualInterestRateAttr?.suffix}
                classNameWrapper="col-2 flex items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
                classNameLabel="w-[--general-2-col-width]"
                isHighlight={true}
              />
            )}
          {market === 'singsaver' &&
            monthlyRepaymentAttr?.label &&
            loanCalculation?.monthlyRepayment && (
              <ProductAttribute
                attributeType={monthlyRepaymentAttr?.attributeType}
                label={monthlyRepaymentAttr?.label}
                prefix={monthlyRepaymentAttr?.prefix}
                suffix={monthlyRepaymentAttr?.suffix}
                value={Number(loanCalculation.monthlyRepayment).toLocaleString(
                  'en-SG',
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  },
                )}
                classNameWrapper="flex items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
                classNameLabel="w-[--general-2-col-width]"
              />
            )}

          {market === 'moneyhero' &&
            totalRepaymentAttr?.label &&
            loanCalculation?.totalRepayment && (
              <ProductAttribute
                attributeType={totalRepaymentAttr?.attributeType}
                label={totalRepaymentAttr?.label}
                prefix={totalRepaymentAttr?.prefix}
                suffix={totalRepaymentAttr?.suffix}
                value={`${Number(loanCalculation.totalRepayment).toLocaleString(
                  'zh-HK',
                  {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  },
                )}`}
                classNameWrapper="flex items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
                classNameLabel="w-[--general-2-col-width]"
              />
            )}

          {market === 'moneyhero' &&
            totalInterestAttr?.label &&
            loanCalculation?.totalInterest && (
              <ProductAttribute
                attributeType={totalInterestAttr?.attributeType}
                label={totalInterestAttr?.label}
                prefix={totalInterestAttr?.prefix}
                suffix={totalInterestAttr?.suffix}
                value={Number(loanCalculation.totalInterest).toLocaleString(
                  'zh-HK',
                  {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  },
                )}
                classNameWrapper="flex items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
                classNameLabel="w-[--general-2-col-width]"
              />
            )}
          <ProductAttribute
            label={staticText.loanTenureText}
            value={loanTenureValue}
            classNameWrapper="flex h-[--button-size-regular] items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
            classNameLabel="w-[--general-2-col-width]"
          />
          {aprAttr?.label && first(loanRateAttr?.fees)?.apr && (
            <ProductAttribute
              attributeType={aprAttr?.attributeType}
              label={aprAttr?.label}
              value={first(loanRateAttr?.fees)?.apr}
              prefix={aprAttr?.prefix}
              suffix={aprAttr?.suffix}
              tooltipContent={aprAttr?.additionalInformation}
              classNameWrapper="flex h-[--button-size-regular] items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
              classNameLabel="w-[--general-2-col-width]"
            />
          )}
          {market === 'singsaver' && (
            <ProductAttribute
              label={staticText.processingFeeText}
              value={`${loanData?.currencyPrefix}${loanCalculation?.totalFees ?? '0.00'}`}
              classNameWrapper="flex h-[--button-size-regular] items-center gap-[--spacing-gap-x] self-stretch px-0 py-[--spacing-padding-y-xs]"
              classNameLabel="w-[--general-2-col-width]"
            />
          )}
        </div>
      </div>
    </>
  );
}

const getAttributeValue = (
  attribute: additionalAttributeType,
): string | number => {
  switch (attribute.attributeFormat) {
    case 'NUMBER':
      return attribute.value === ''
        ? (attribute?.emptyValueText ?? '')
        : Number(attribute.value).toLocaleString('en-SG', {
            minimumFractionDigits: Number(attribute.value) < 1000 ? 2 : 0,
            maximumFractionDigits: Number(attribute.value) < 1000 ? 2 : 0,
          });
    case 'NUMBER_RANGE':
      return formatRangeValue(attribute?.rangeValue);
    default:
      return attribute.value ?? '';
  }
};

const formatRangeValue = (rangeValue?: RangeValue | null): string => {
  if (!rangeValue) return '';

  const { minimum, maximum } = rangeValue;

  if (minimum !== undefined && maximum !== undefined) {
    return `${minimum} - ${maximum}`;
  }
  return minimum !== undefined
    ? `${minimum}`
    : maximum !== undefined
      ? `${maximum}`
      : '';
};

const loanTenureToMonths = (tenure: number | undefined): string => {
  if (tenure === undefined) return '';
  return `${tenure} month${tenure === 1 ? '' : 's'}`;
};

const prefixAttribute = (prefixText: string | undefined): string => {
  if (!prefixText) return '';
  const isCurrencyPrefix = ['S$', 'HK$', '₱'];
  return isCurrencyPrefix.includes(prefixText) ? prefixText : `${prefixText} `;
};

const suffixAttribute = (suffixText: string | undefined): string => {
  if (!suffixText) return '';
  return suffixText.startsWith('%') ? suffixText : ` ${suffixText}`;
};

function RenderPromoCodeReminderPopup(props: {
  staticText: Translated<typeof RewardProductText>;
  setOpen: (value: boolean) => void;
  promoCode: string;
}) {
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);
  const handleCopyPromoCode = async () => {
    try {
      await navigator.clipboard.writeText(props.promoCode);
    } catch (err) {
      console.error('Failed to copy promo code:', err);
    }
  };

  return (
    <div className="fixed left-0 top-0 z-50 h-screen w-screen">
      <div className="fixed inset-0 flex items-center justify-center bg-black/50">
        <div className="mhg-alpha-box-shadow-2 flex max-w-[--general-6-col-width] flex-col gap-[--spacing-gap-y] rounded-[--radius-card] px-[--spacing-padding-x] py-[--spacing-padding-y] bg-[--fill-card]">
          <div className="mhg-alpha-title-1 text-[--text-primary]">
            {props.staticText.promoCodeReminderPopupTitle}
          </div>
          <div className="mhg-alpha-typography-body-1 text-[--text-primary]">
            {props.staticText.promoCodeReminderPopupDescription}
          </div>
          {props.promoCode && (
            <div className="flex origin-left items-center gap-spacing/gap/soft-4px">
              <p className="mhg-alpha-typography-body-2 md:mhg-alpha-typography-body-1 text-nowrap text-text/secondary-lighter">
                {props.staticText.promoCode}:
              </p>
              <p className="mhg-alpha-typography-bullet-2 md:mhg-alpha-typography-bullet-1 text-fill/primary">
                {props.promoCode}
              </p>
              <div
                role="button"
                tabIndex={0}
                aria-label={props.staticText.promoCodeReminderPopupCopyButton}
                onClick={() => {
                  handleCopyPromoCode();
                  setShowCopiedTooltip(true);
                  setTimeout(() => {
                    setShowCopiedTooltip(false);
                  }, 1500);
                }}
              >
                <ContentCopy className="cursor-pointer" />
                {showCopiedTooltip && (
                  <div className="mhg-alpha-typography-caption absolute z-10 rounded p-2 shadow-lg bg-white text-text/secondary-lighter">
                    {props.staticText.copied}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="flex flex-col gap-[--spacing-gap-y-sm] self-stretch md:flex-row md:items-center md:justify-center">
            <button
              type="button"
              className="mhg-alpha-btn-outline-neutral w-full md:w-auto md:flex-1"
              onClick={() => {
                props.setOpen(false);
              }}
            >
              {props.staticText.promoCodeReminderPopupDismissButton}
            </button>
            <button
              type="button"
              className="mhg-alpha-btn-primary mt-2 w-full md:mt-0 md:w-auto md:flex-1"
              onClick={() => {
                handleCopyPromoCode();
                props.setOpen(false);
              }}
            >
              {props.staticText.promoCodeReminderPopupCopyButton}
              <ArrowRightAlt className="ml-1 size-shapes/icon-size-lg" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RenderTwoStepApplicationContent(props: {
  productAttributes: LeadCapturePage_attributeList['attributeList'];
  twoStepApplicationContent?: {
    applicationTitle?: string;
    applicationSubtitle?: string;
    applicationFooter?: string;
    productSectionTitle?: string;
  };
}): JSX.Element {
  const bundleProductOptions = props.productAttributes?.find(
    (item) => item?.attributeType === 'BUNDLE_PRODUCT_OPTIONS',
  ) as additionalAttributeType;

  const productUids: string[] =
    (bundleProductOptions as LeadCapturePage_keyValueTextAttribute)?.keyValue
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ?.flatMap((kv: any) => kv.values) || [];

  return (
    <div className="flex w-full flex-col gap-[--spacing-gap-y]">
      <div className="mhg-alpha-rewards-lead-capture-heading-container">
        <div className="mhg-alpha-display-1 not-italic text-[--text-primary]">
          {props.twoStepApplicationContent?.applicationTitle}
        </div>
        {props.twoStepApplicationContent?.applicationSubtitle && (
          <div className="mhg-alpha-rewards-lead-capture-subheading">
            {props.twoStepApplicationContent?.applicationSubtitle}
          </div>
        )}
      </div>
      <div className="mhg-alpha-rewards-lead-capture-card max-w-[calc(95vw-var(--spacing-padding-x-sm))] bg-[--fill-card] md:max-w-screen">
        <div
          // eslint-disable-next-line tailwindcss/classnames-order
          className={classNames(
            'flex w-full overflow-x-auto snap-x snap-mandatory py-[--spacing-padding-y-sm] gap-[--spacing-gap-y-sm] no-scrollbar scroll-pl-[--spacing-padding-x-sm]',
            {
              'md:grid md:grid-cols-2 md:px-[--spacing-padding-x-sm] md:overflow-visible':
                productUids.length >= 2,
              'md:grid-cols-1 md:px-[--spacing-padding-x-sm]':
                productUids.length === 1,
            },
          )}
        >
          <div
            className="w-[--spacing-padding-x-sm] flex-none md:hidden"
            aria-hidden="true"
          />
          {productUids.slice(0, 4).map((uid) => (
            <div
              key={uid}
              // eslint-disable-next-line tailwindcss/classnames-order
              className={classNames(
                'basis-[31%] snap-start overflow-hidden flex-none',
                'md:basis-auto md:shrink md:overflow-visible',
              )}
            >
              <BundleProductCard productUid={uid} />
            </div>
          ))}
          <div
            className="w-[--spacing-padding-x-sm] flex-none md:hidden"
            aria-hidden="true"
          />
        </div>
        {props.twoStepApplicationContent?.applicationFooter && (
          <div className="mhg-alpha-rewards-lead-capture-card-footer flex flex-row items-start gap-[--spacing-gap-soft-12px] self-stretch px-[--spacing-padding-x] py-[--spacing-padding-y-sm]">
            <InfoCircle
              aria-hidden="true"
              className="inline size-shapes/icon-size-md text-[color:--colors-neutral-gray-700] text-[length:--shapes-icon-size-md]"
            />
            <div
              className="font-[--font-weight-button-1] text-[--buttons-neutral-dark] text-font-size/md leading-[--line-height-button-2]"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html:
                  props.twoStepApplicationContent?.applicationFooter ?? '',
              }}
            ></div>
          </div>
        )}
      </div>
    </div>
  );
}

function BundleProductCard({ productUid }: { productUid: string }) {
  const [loading, setLoading] = useState(true);
  const [productName, setProductName] = useState<string | undefined>(undefined);
  const [productBundleText, setProductBundleText] = useState<
    string | undefined
  >(undefined);
  const [productIcon, setProductIcon] = useState<string | undefined>(undefined);
  const hasFetchedRef = useRef(false);
  const [showTooltip, setShowTooltip] = useState(false);
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    const fetchProduct = async () => {
      try {
        const query = createLazyQuery(GetProductByUidOperation);
        const response = await query({ productUid });
        const product = response?.productByUid;
        setProductName(product?.productName);
        const productBundleText = product?.attributeList?.find(
          (item) => item?.attributeType === 'CC_BUNDLE_TEXT',
        ) as additionalAttributeType;
        setProductBundleText(productBundleText?.value?.toString());
        const productIcon = product?.attributeList?.find(
          (item) => item?.attributeType === 'PRODUCT_ICON',
        ) as additionalAttributeType;
        setProductIcon(productIcon?.value?.toString());
        setLoading(false);
      } catch (error) {
        console.error(
          `Failed to fetch product with productUid "${productUid}"`,
          error,
        );
      }
    };
    fetchProduct();
  }, [productUid]);

  if (loading) {
    return (
      <div className="flex flex-col gap-3 rounded-[--radius-card-inner] border border-solid border-[--borders-default] p-6">
        <div className="flex animate-pulse flex-col items-center gap-3 md:items-start">
          <div className="size-[--shapes-icon-size-lg] rounded-full bg-gray-200" />
          <div className="h-5 w-3/4 rounded bg-gray-200" />
        </div>
        <div className="animate-pulse space-y-2">
          <div className="h-3 w-full rounded bg-gray-100" />
          <div className="h-3 w-5/6 rounded bg-gray-100" />
        </div>
      </div>
    );
  }

  if (!productName && !productBundleText) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 rounded-[--radius-card-inner] border border-solid border-[--borders-default] p-6">
      <div
        className="flex flex-col items-center gap-3 md:items-start"
        role="button"
        tabIndex={0}
        onClick={() => setShowTooltip((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setShowTooltip((prev) => !prev);
          }
        }}
      >
        <div
          className="size-[--shapes-icon-size-lg]"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: productIcon ?? '',
          }}
        ></div>
        <h3 className="mhg-alpha-typography-title-2 text-center md:text-left">
          {productName || ''}
        </h3>
        {productBundleText && (
          <p className="mhg-alpha-typography-body-2 hidden md:block">
            {productBundleText}
          </p>
        )}
        {showTooltip && (
          <div className="mhg-alpha-typography-caption absolute z-10 block rounded p-2 shadow-lg bg-white text-text/secondary-lighter md:hidden">
            {productBundleText}
          </div>
        )}
      </div>
    </div>
  );
}
